import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Snackbar,
  CircularProgress,
  Alert,
  Tab,
  TextField,
  InputAdornment,
  Typography
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import { useLocaleText } from 'src/helper/localeText';
import {
  StyledTabs,
  TabsWrapper
} from 'src/components/styledHelpComponents/styledHelpComponents';
import { Edit, Delete } from '@mui/icons-material';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';
import UserEditModal from './userEditDialog';
import AddUserModal from './addUserDialog';
import ResetPasswordModal from '../components/resetPasswordModal';
import RefreshIcon from '@mui/icons-material/Refresh';

/* ADMIN STRANA ZA USERE */
const Users = () => {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [selectedTab, setSelectedTab] = useState('all');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [rowCount, setRowCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation();
  const localeText = useLocaleText();
  const [roles, setRoles] = useState([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openResetPasswordModal, setOpenResetPasswordModal] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState(null);

  const handleAddModalToggle = (open) => {
    setOpenAddModal(open);
  };

  const handleConfirmDeleteToggle = (open, user) => {
    setOpenConfirmDeleteDialog(open);
    if (open) setSelectedUser(user);
  };

  const fetchRoles = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/roles.php`);
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setSnackbarMessage(t('error.genericError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [t]);

  // Definisanje fetchUsersAndCategories koristeći useCallback za memoizaciju
  const fetchUsersAndCategories = useCallback(async () => {
    setLoading(true);
    try {
      const usersUrl = new URL(`${API_BASE_URL}/api/users.php`);
      usersUrl.searchParams.append('limit', pageSize);
      usersUrl.searchParams.append('offset', (page - 1) * pageSize);

      if (selectedTab !== 'all') {
        usersUrl.searchParams.append('role', selectedTab);
      }

      if (searchQuery) {
        usersUrl.searchParams.append('search', searchQuery);
      }

      const response = await axios.get(usersUrl.toString());
      const { users, total } = response.data;

      setUsers(users);
      setRowCount(parseInt(total, 10));
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(t('error.failedToLoadData')); // Dodaj odgovarajući prevod
    } finally {
      setLoading(false);
    }
  }, [pageSize, page, selectedTab, searchQuery, t]);

  // useEffect za dobijanje podataka
  useEffect(() => {
    fetchUsersAndCategories();
    fetchRoles();
  }, [fetchUsersAndCategories, fetchRoles]);

  // Definisanje funkcije handleEditModalToggle
  const handleEditModalToggle = (open, user) => {
    setSelectedUser(user);
    setOpenEditModal(open);
  };

  // Definisanje funkcije handleOpenResetPasswordModal
  const handleOpenResetPasswordModal = (user) => {
    setSelectedUserForReset(user);
    setOpenResetPasswordModal(true);
  };

  // Definisanje kolona za DataGrid
  const columns = useMemo(
    () => [
      { field: 'ID', headerName: t('columns.id'), width: 90 },
      {
        field: 'userName',
        headerName: t('columns.userName'),
        width: 150
      },
      {
        field: 'userPass',
        headerName: t('columns.userPass'),
        width: 150,
        renderCell: () => '********'
      },
      {
        field: 'isAdmin',
        headerName: t('columns.isAdmin'),
        width: 100,
        renderCell: (params) =>
          params.value === '1' ? t('dialog.yes') : t('dialog.no')
      },
      {
        field: 'role',
        headerName: t('adminDashboard.roles'),
        width: 200,
        renderCell: (params) => {
          const translatedRole = t(`roles.${params.row.role_name}`);
          return translatedRole;
        }
      },
      {
        field: 'userRemark',
        headerName: t('columns.userRemark'),
        width: 150
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: t('columns.actions'),
        width: 400,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<Edit sx={{ color: '#1c75bc', fontSize: '24px' }} />}
            label="Edit"
            onClick={() => handleEditModalToggle(true, params.row)}
          />,
          <GridActionsCellItem
            icon={<Delete sx={{ color: '#e91e63', fontSize: '24px' }} />}
            label="Delete"
            onClick={() => handleConfirmDeleteToggle(true, params.row)}
          />,
          <GridActionsCellItem
            icon={
              <Box
                sx={{
                  backgroundColor: '#2e7087b8',
                  color: '#fff',
                  padding: '5px 10px',
                  textTransform: 'none',
                  borderRadius: '6px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer', // Da izgleda kao dugme
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  }
                }}
              >
                <RefreshIcon sx={{ marginRight: '5px' }} />
                {t('dialog.password')}
              </Box>
            }
            label={t('dialog.resetPasswordButton')}
            onClick={() => handleOpenResetPasswordModal(params.row)}
          />
        ]
      }
    ],
    [t, handleEditModalToggle, handleConfirmDeleteToggle]
  );

  // Funkcija za promenu taba
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPage(1);
  };

  // Funkcija za promenu pretrage
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Funkcija za zatvaranje Snackbar-a
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Funkcija za uređivanje korisnika
  const handleEditUser = async (updatedUser) => {
    try {
      const prepareUser = {
        userName: updatedUser.userName,
        isAdmin: updatedUser.isAdmin,
        role: updatedUser.role,
        userRemark: updatedUser.userRemark
      };

      await axios.put(
        `${API_BASE_URL}/api/users.php?id=${updatedUser.ID}`,
        prepareUser
      );
      setSnackbarMessage(t('adminModovi.saveSuccess'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenEditModal(false);
      fetchUsersAndCategories();
    } catch (error) {
      console.error('Error updating user:', error);
      setSnackbarMessage(t('error.genericError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Funkcija za čuvanje novog korisnika
  const handleSaveUser = useCallback(
    async (newUser) => {
      try {
        await axios.post(`${API_BASE_URL}/api/users.php?add=true`, newUser);
        setSnackbarMessage(t('adminModovi.saveSuccess'));
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchUsersAndCategories();
        return true;
      } catch (error) {
        console.error('Error creating user:', error);
        if (
          error.response &&
          error.response.data &&
          error.response.data.error &&
          error.response.data.error.includes('Duplicate entry')
        ) {
          console.log(error.response.data.error);
          //Query failed: SQLSTATE[23000]: Integrity constraint violation: 1062 Duplicate entry 'testeee' for key 'userName'

          setSnackbarMessage(t('errors.duplicateUserName')); // Dodaj prevod za ovu poruku u i18n fajlove
        } else {
          setSnackbarMessage(t('error.genericError'));
        }
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return false;
      }
    },
    [t, fetchUsersAndCategories]
  );

  // Funkcija za brisanje korisnika
  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/users.php?id=${userId}`);
      setSnackbarMessage(t('dialog.successfullyDeleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setOpenConfirmDeleteDialog(false);
      fetchUsersAndCategories();
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbarMessage(t('error.genericError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Prikaz greške ako postoji
  if (error) {
    return (
      <Box m="20px">
        <Typography variant="h4">{t('meni.users')}</Typography>
        <Box m="40px 0 0 0">
          <p>{error}</p>
        </Box>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box>
        <Button
          onClick={() => handleAddModalToggle(true)}
          sx={{
            marginBottom: '20px',
            backgroundColor: '#25be6c',
            '&:hover': {
              backgroundColor: '#1c75bc'
            },
            color: '#ffffff',
            borderRadius: '8px',
            padding: '10px 20px',
            fontWeight: 'bold'
          }}
        >
          {t('button.addUser')}
        </Button>

        <AddUserModal
          open={openAddModal}
          onClose={() => handleAddModalToggle(false)}
          onSave={handleSaveUser}
          roles={roles}
        />
      </Box>
      <Typography
        variant="h5"
        sx={{
          marginBottom: '10px',
          fontWeight: 'bold',
          color: '#333',
          textAlign: 'left'
        }}
      >
        {t('headers.userManagement')}
      </Typography>

      <TabsWrapper sx={{ width: '136vh !important' }}>
        <StyledTabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label={t('tabs.users')}
          sx={{
            backgroundColor: '#f5f7fa',
            borderRadius: '8px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            marginBottom: '20px'
          }}
        >
          <Tab label={t('tabs.allUsers')} value="all" />
          <Tab label={t('tabs.admins')} value="admin" />
          <Tab label={t('tabs.schools')} value="skola" />
          <Tab label={t('tabs.dms')} value="dms" />
        </StyledTabs>
      </TabsWrapper>

      <TextField
        variant="outlined"
        placeholder={t('search.placeholder')}
        value={searchQuery}
        onChange={handleSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
        sx={{
          width: '136vh',
          backgroundColor: '#f5f7fa',
          borderRadius: '8px',
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px'
          },
          marginBottom: '20px'
        }}
      />

      <Box
        m="0px 0 0 0"
        height="75vh"
        width="136vh"
        sx={{ backgroundColor: '#ffffff' }}
      >
        {loading ? (
          <CircularProgress
            sx={{ display: 'block', margin: '20px auto', color: '#4facfe' }}
          />
        ) : (
          <DataGrid
            rows={Array.isArray(users) ? users : []}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            paginationMode="server"
            rowCount={rowCount}
            onPageChange={(newPage) => setPage(newPage + 1)}
            page={page - 1}
            disableColumnMenu
            localeText={localeText}
            getRowId={(row) => row.ID}
            componentsProps={{
              pagination: {
                labelRowsPerPage: t('pagination.rowsPerPage'),
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from}-${to} ${t('pagination.of')} ${
                    count !== -1 ? count : `${t('pagination.moreThan')} ${to}`
                  }`
              }
            }}
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '10px',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
              },
              '& .MuiDataGrid-cell': {
                borderBottom: 'none',
                padding: '12px',
                fontSize: '14px',
                color: '#333'
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#2596be',
                color: '#ffffff',
                borderBottom: 'none',
                borderRadius: '12px 12px 0 0'
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: '#1c75bc',
                color: '#ffffff',
                borderTop: 'none',
                borderRadius: '0 0 12px 12px'
              },
              '& .MuiCheckbox-root': {
                color: '#28a745 !important'
              },
              '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
                color: '#ffffff !important'
              }
            }}
          />
        )}
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ borderRadius: '8px' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <ConfirmDeleteDialog
        open={openConfirmDeleteDialog}
        onClose={() => handleConfirmDeleteToggle(false, null)}
        onConfirm={() => handleDeleteUser(selectedUser?.ID)}
      />
      <UserEditModal
        open={openEditModal}
        user={selectedUser}
        roles={roles}
        onClose={() => handleEditModalToggle(false, null)}
        onSave={handleEditUser}
      />
      {openResetPasswordModal && (
        <ResetPasswordModal
          open={openResetPasswordModal}
          onClose={() => setOpenResetPasswordModal(false)}
          user={selectedUserForReset}
        />
      )}
    </Box>
  );
};

export default Users;
